import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Cart } from "@repo/shared-types";
import { buttonStyles, displayTitleStyle, eyebrowStyle, heroPanelStyle, panelStyle, subtleText, tokens } from "../lib/ui";

export function CartPage() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.getCart()
      .then(setCart)
      .catch((err) => {
        console.error(err);
        setNotice(err instanceof Error ? err.message : "Failed to load cart");
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleUpdateQty(productId: number, quantity: number) {
    try {
      setBusy(true);
      setNotice("");
      const updated = await api.updateCartItem(productId, quantity);
      setCart(updated);
      window.dispatchEvent(new Event("cart:updated"));
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to update item");
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    try {
      setBusy(true);
      await api.clearCart();
      setCart(null);
      window.dispatchEvent(new Event("cart:updated"));
      setNotice("Cart cleared.");
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Failed to clear cart");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="page-loading">Loading your cart…</div>;

  const items = cart?.items ?? [];
  const total = items.reduce((sum, item) => {
    const price = Number(item.price ?? "0");
    if (Number.isFinite(price)) return sum + price * item.quantity;
    return sum;
  }, 0);

  return (
    <div className="inner-page__container" style={{ gap: tokens.space.lg }}>
      <section
        style={{
          ...heroPanelStyle,
          padding: tokens.space.lg,
        }}
      >
        <p style={eyebrowStyle}>Cart Review</p>
        <h2 style={{ ...displayTitleStyle, margin: "0.35rem 0", fontSize: tokens.fontSize["3xl"] }}>Shopping cart</h2>
        <p style={{ ...subtleText, maxWidth: 700 }}>Review quantities before checkout. Cart changes sync immediately with the worker API.</p>
      </section>

      {notice && (
        <p
          style={{
            margin: 0,
            color: notice.includes("Failed") ? tokens.color.error : tokens.color.success,
            background: notice.includes("Failed") ? tokens.color.errorLight : tokens.color.successLight,
            border: `1px solid ${notice.includes("Failed") ? "#fecaca" : "#bbf7d0"}`,
            borderRadius: tokens.radius.md,
            padding: "0.8rem 1rem",
          }}
        >
          {notice}
        </p>
      )}

      {items.length === 0 ? (
        <section style={{ ...panelStyle, background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,251,255,0.92))" }}>
          <h3 style={{ marginTop: 0 }}>Your cart is empty</h3>
          <p style={subtleText}>Add products from the catalog to continue.</p>
          <button type="button" style={buttonStyles.primary} onClick={() => navigate("/shop")}>Browse products</button>
        </section>
      ) : (
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.9fr) minmax(280px, 0.9fr)",
            gap: tokens.space.md,
            alignItems: "start",
          }}
        >
          <div style={{ ...panelStyle, overflowX: "auto", background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,251,255,0.92))" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={headerCellStyle}>Product</th>
                  <th style={{ ...headerCellStyle, textAlign: "center" }}>Qty</th>
                  <th style={{ ...headerCellStyle, textAlign: "right" }}>Unit price</th>
                  <th style={{ ...headerCellStyle, textAlign: "right" }}>Subtotal</th>
                  <th style={{ ...headerCellStyle, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const unitPrice = Number(item.price ?? "0");
                  const subtotal = Number.isFinite(unitPrice) ? unitPrice * item.quantity : NaN;
                  const canIncrease = busy ? false : item.available_quantity ? item.quantity < item.available_quantity : true;
                  const canDecrease = !busy && item.quantity > 1;
                  return (
                    <tr key={item.product_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={bodyCellStyle}>
                        <div style={{ display: "flex", gap: "0.9rem", alignItems: "center" }}>
                          <div style={itemThumbStyle}>
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name ?? `Product ${item.product_id}`} style={itemThumbImageStyle} />
                            ) : (
                              <span aria-hidden="true" style={{ fontSize: "1.4rem" }}>🕉️</span>
                            )}
                          </div>
                          <div style={{ display: "grid", gap: "0.3rem" }}>
                            <strong>{item.name ?? `Product #${item.product_id}`}</strong>
                            {item.slug && <span style={itemMetaStyle}>{item.slug.replace(/-/g, " ")}</span>}
                            <span style={itemMetaStyle}>
                              {item.available_quantity && item.available_quantity > 0
                                ? `${item.available_quantity} available right now`
                                : item.in_stock === false
                                  ? "Available to order"
                                  : "Ready to ship"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                        <div style={{ display: "inline-flex", border: "1px solid rgba(23,32,51,0.16)", borderRadius: tokens.radius.md, background: "rgba(255,255,255,0.9)" }}>
                          <button
                            type="button"
                            disabled={!canDecrease}
                            style={qtyButtonStyle}
                            onClick={() => handleUpdateQty(item.product_id, item.quantity - 1)}
                          >
                            -
                          </button>
                          <span style={{ padding: "0.5rem 0.75rem", minWidth: 36, textAlign: "center" }}>{item.quantity}</span>
                          <button
                            type="button"
                            disabled={!canIncrease}
                            style={qtyButtonStyle}
                            onClick={() => handleUpdateQty(item.product_id, item.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td style={{ ...bodyCellStyle, textAlign: "right" }}>${unitPrice.toFixed(2)}</td>
                      <td style={{ ...bodyCellStyle, textAlign: "right", fontWeight: tokens.fontWeight.semibold }}>
                        {Number.isFinite(subtotal) ? `$${subtotal.toFixed(2)}` : "-"}
                      </td>
                      <td style={{ ...bodyCellStyle, textAlign: "right" }}>
                        <button
                          type="button"
                          disabled={busy}
                          style={buttonStyles.danger}
                          onClick={() => handleUpdateQty(item.product_id, 0)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <aside style={{ ...panelStyle, display: "grid", gap: "1rem", background: "linear-gradient(180deg, rgba(255,250,240,0.98), rgba(255,255,255,0.95))" }}>
            <div>
              <p style={eyebrowStyle}>Order Summary</p>
              <h3 style={{ margin: "0.35rem 0 0.5rem" }}>Ready for checkout</h3>
              <p style={subtleText}>Cart updates are saved instantly. Shipping and payment details come next.</p>
            </div>

            <div style={{ display: "grid", gap: "0.6rem" }}>
              <SummaryRow label="Items" value={`${items.reduce((sum, item) => sum + item.quantity, 0)} selected`} />
              <SummaryRow label="Subtotal" value={`$${total.toFixed(2)}`} />
              <SummaryRow label="Shipping" value="Calculated at checkout" />
            </div>

            <div style={{ borderTop: "1px solid rgba(23,32,51,0.08)", paddingTop: "1rem", display: "grid", gap: "0.75rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.75rem" }}>
                <span style={{ fontWeight: tokens.fontWeight.semibold }}>Estimated total</span>
                <strong style={{ fontSize: tokens.fontSize["2xl"] }}>${total.toFixed(2)}</strong>
              </div>
              <button type="button" disabled={busy} onClick={() => navigate("/checkout")} style={buttonStyles.primary}>
                Proceed to checkout
              </button>
              <button type="button" disabled={busy} onClick={() => navigate("/shop")} style={buttonStyles.secondary}>
                Continue shopping
              </button>
              <button type="button" disabled={busy} onClick={handleClear} style={buttonStyles.danger}>
                Clear cart
              </button>
            </div>
          </aside>

        </section>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", color: "#334155" }}>
      <span>{label}</span>
      <span style={{ fontWeight: tokens.fontWeight.semibold }}>{value}</span>
    </div>
  );
}

const headerCellStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.7rem 0.5rem",
  color: "#475569",
  fontSize: tokens.fontSize.sm,
  fontWeight: tokens.fontWeight.semibold,
};

const bodyCellStyle: React.CSSProperties = {
  padding: "0.85rem 0.5rem",
  verticalAlign: "middle",
};

const qtyButtonStyle: React.CSSProperties = {
  border: "none",
  background: "rgba(248,250,252,0.95)",
  minWidth: 30,
  cursor: "pointer",
};

const itemThumbStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: tokens.radius.md,
  overflow: "hidden",
  background: "linear-gradient(135deg, #fff3d8 0%, #ffe8ba 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const itemThumbImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const itemMetaStyle: React.CSSProperties = {
  fontSize: tokens.fontSize.sm,
  color: "#64748b",
};
